import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFlightsStore } from '../store/flights';
import { usePostsStore, Post, PostStatus } from '../store/posts';
import { useMediaStore } from '../store/media';
import { useProjectsStore } from '../store/projects';
import { EditFlightModal } from '../components/EditFlightModal';
import { PageHeader } from '../components/PageHeader';
import { getMediaUrl } from '../services/api';
import { POST_STATUS, FLIGHT_STATUS } from '../config/statusConfig';

type TabType = 'overview' | 'posts' | 'media';

export function FlightDetailPage() {
  const { t } = useTranslation(['flights', 'posts']);
  const { flightId } = useParams<{ flightId: string }>();
  const navigate = useNavigate();

  const { currentFlight, fetchFlight, updateFlight, isLoading: flightLoading } = useFlightsStore();
  const { posts, fetchPosts } = usePostsStore();
  const { media, fetchMedia } = useMediaStore();
  const { currentProject, fetchProject } = useProjectsStore();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [statusFilter, setStatusFilter] = useState<PostStatus | 'all'>('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Load flight data
  useEffect(() => {
    if (flightId) {
      fetchFlight(flightId);
    }
  }, [flightId, fetchFlight]);

  // Load project, posts and media when flight is loaded
  useEffect(() => {
    if (currentFlight?.project_id) {
      fetchProject(currentFlight.project_id);
      fetchPosts(currentFlight.project_id);
      fetchMedia(currentFlight.project_id);
    }
  }, [currentFlight?.project_id, fetchProject, fetchPosts, fetchMedia]);

  // Filter posts by flight
  const flightPosts = useMemo(() => {
    return posts.filter(post => post.flight_id === flightId);
  }, [posts, flightId]);

  // Filter media by flight
  const flightMedia = useMemo(() => {
    return media.filter(m => m.flight_id === flightId);
  }, [media, flightId]);

  // Apply status filter
  const filteredPosts = useMemo(() => {
    if (statusFilter === 'all') return flightPosts;
    return flightPosts.filter(post => post.status === statusFilter);
  }, [flightPosts, statusFilter]);

  // Group posts by status for overview
  const postsByStatus = useMemo(() => {
    const grouped: Record<PostStatus, Post[]> = {
      draft: [],
      scheduled: [],
      publishing: [],
      published: [],
      partial: [],
      failed: [],
      cancelled: [],
    };
    flightPosts.forEach(post => {
      grouped[post.status].push(post);
    });
    return grouped;
  }, [flightPosts]);

  // Parse date string as local date (avoiding timezone issues)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Calculate date range
  const dateRange = useMemo(() => {
    if (!currentFlight) return null;
    const start = parseLocalDate(currentFlight.start_date);
    const end = parseLocalDate(currentFlight.end_date);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for comparison
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const progress = Math.min(100, Math.max(0, (daysElapsed / totalDays) * 100));
    return { start, end, totalDays, daysElapsed, progress };
  }, [currentFlight]);

  const formatDate = (date: string | Date) => {
    // If string, parse as local date to avoid timezone shift
    if (typeof date === 'string') {
      const [year, month, day] = date.split('T')[0].split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      return localDate.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: string | Date) => {
    return new Date(date).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (flightLoading || !currentFlight) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 w-96 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
        title={currentFlight.name}
        subtitle={currentFlight.description || undefined}
        breadcrumbs={currentProject ? [
          { label: t('common:projects', 'Projekte'), href: '/projects' },
          { label: currentProject.name, href: `/projects/${currentProject.id}` },
          { label: t('title'), href: '/flights' },
        ] : [
          { label: t('title'), href: '/flights' },
        ]}
      />

      {/* Flight Details Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {/* Color indicator */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: currentFlight.color || '#3B82F6' }}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{currentFlight.name}</h1>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${FLIGHT_STATUS[currentFlight.status].bg} ${FLIGHT_STATUS[currentFlight.status].text}`}>
                  <span className={`w-2 h-2 rounded-full ${FLIGHT_STATUS[currentFlight.status].dot}`} />
                  {t(`flights:status.${currentFlight.status}`)}
                </span>
              </div>
              {currentFlight.description && (
                <p className="text-gray-600 mt-1">{currentFlight.description}</p>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(currentFlight.start_date)} - {formatDate(currentFlight.end_date)}
                </span>
                <span className="text-gray-300">|</span>
                <span className="font-mono text-xs">{currentFlight.display_id}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Edit Button */}
            <button
              onClick={() => setShowEditModal(true)}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('edit')}
            </button>

            {/* Archive/Unarchive Button */}
            {currentFlight.status !== 'archived' ? (
              <button
                onClick={async () => {
                  if (!confirm(t('confirmArchive'))) return;
                  setActionLoading(true);
                  try {
                    await updateFlight(currentFlight.id, { status: 'archived' as never });
                    fetchFlight(currentFlight.id);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                {t('archive')}
              </button>
            ) : (
              <button
                onClick={async () => {
                  setActionLoading(true);
                  try {
                    await updateFlight(currentFlight.id, { status: 'draft' as never });
                    fetchFlight(currentFlight.id);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading}
                className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('restore')}
              </button>
            )}

            {/* Close Button */}
            <button
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-gray-600 ml-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {dateRange && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-500">{t('progress')}</span>
              <span className="text-gray-700 font-medium">
                {dateRange.daysElapsed > 0 ? t('dayOf', { current: Math.min(dateRange.daysElapsed, dateRange.totalDays), total: dateRange.totalDays }) : t('startsSoon')}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${dateRange.progress}%`,
                  backgroundColor: currentFlight.color || '#3B82F6',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {[
            { id: 'overview' as TabType, label: t('overview'), icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z' },
            { id: 'posts' as TabType, label: `${t('posts')} (${flightPosts.length})`, icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
            { id: 'media' as TabType, label: `${t('media')} (${flightMedia.length})`, icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Actions Bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-500 mr-2">{t('quickActions')}:</span>
              {currentProject && (
                <Link
                  to={`/projects/${currentProject.id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('toCalendar')}
                </Link>
              )}
              <Link
                to={`/posts?flight=${currentFlight.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('allPosts')} ({flightPosts.length})
              </Link>
              <Link
                to="/creator"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-medium rounded-lg hover:bg-purple-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('createDesign')}
              </Link>
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors ml-auto"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('settings')}
              </button>
            </div>
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Goal Progress */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('postGoal')}
              </div>
              {currentFlight.goals?.post_count ? (
                <>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-2xl font-bold ${flightPosts.length >= currentFlight.goals.post_count ? 'text-green-600' : 'text-gray-900'}`}>
                      {flightPosts.length}
                    </span>
                    <span className="text-gray-400">/ {currentFlight.goals.post_count}</span>
                    {flightPosts.length >= currentFlight.goals.post_count && (
                      <svg className="w-5 h-5 text-green-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div
                      className={`h-full rounded-full transition-all ${flightPosts.length >= currentFlight.goals.post_count ? 'bg-green-500' : ''}`}
                      style={{
                        width: `${Math.min(100, (flightPosts.length / currentFlight.goals.post_count) * 100)}%`,
                        backgroundColor: flightPosts.length >= currentFlight.goals.post_count ? undefined : (currentFlight.color || '#3B82F6'),
                      }}
                    />
                  </div>
                </>
              ) : (
                <span className="text-gray-400 text-sm">{t('noGoalDefined')}</span>
              )}
            </div>

            {/* Scheduled Posts */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('scheduled')}
              </div>
              <span className="text-2xl font-bold text-blue-600">{postsByStatus.scheduled.length}</span>
              {postsByStatus.scheduled.length === 0 && currentFlight.status === 'active' && (
                <p className="text-amber-600 text-xs mt-1 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {t('noScheduledPosts')}
                </p>
              )}
            </div>

            {/* Published Posts */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('published')}
              </div>
              <span className="text-2xl font-bold text-green-600">{postsByStatus.published.length}</span>
            </div>

            {/* Media Files */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('media')}
              </div>
              <span className="text-2xl font-bold text-gray-900">{flightMedia.length}</span>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Next Scheduled Post - Prominent */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('nextPost')}
              </h3>
              {(() => {
                const nextPost = postsByStatus.scheduled
                  .filter(p => new Date(p.scheduled_for) > new Date())
                  .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime())[0];

                if (!nextPost) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>{t('noScheduledPostsShort')}</p>
                      {currentProject && (
                        <Link
                          to={`/projects/${currentProject.id}`}
                          className="inline-block mt-3 text-sm text-blue-600 hover:text-blue-700"
                        >
                          {t('createPost')} →
                        </Link>
                      )}
                    </div>
                  );
                }

                const timeUntil = new Date(nextPost.scheduled_for).getTime() - new Date().getTime();
                const daysUntil = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
                const hoursUntil = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                return (
                  <button
                    onClick={() => setSelectedPost(nextPost)}
                    className="w-full text-left flex gap-4 p-3 -m-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {/* Thumbnail */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {nextPost.media_urls?.[0] ? (
                        <img
                          src={getMediaUrl(nextPost.media_urls[0])}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          {daysUntil > 0 ? `${t('in')} ${daysUntil}d ${hoursUntil}h` : hoursUntil > 0 ? `${t('in')} ${hoursUntil}h` : t('common:soon', 'Bald')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {nextPost.platforms?.join(', ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-gray-900 line-clamp-2 mb-2">
                        {nextPost.text || <span className="text-gray-400 italic">{t('noText')}</span>}
                      </p>
                      <div className="text-sm text-gray-500">
                        {new Date(nextPost.scheduled_for).toLocaleString('de-DE', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0 self-center" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })()}
            </div>

            {/* Post Status Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('statusDistribution')}
              </h3>
              <div className="space-y-3">
                {(['published', 'scheduled', 'draft', 'failed'] as PostStatus[]).map((status) => {
                  const count = postsByStatus[status].length;
                  const percentage = flightPosts.length > 0 ? (count / flightPosts.length) * 100 : 0;
                  return (
                    <button
                      key={status}
                      onClick={() => {
                        if (count > 0) {
                          setStatusFilter(status);
                          setActiveTab('posts');
                        }
                      }}
                      disabled={count === 0}
                      className={`w-full text-left ${count > 0 ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default opacity-60'} rounded-lg transition-colors`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${POST_STATUS[status].dot}`} />
                          <span className="text-sm text-gray-700">{t(`posts:status.${status}`)}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{count}</span>
                      </div>
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${POST_STATUS[status].dot}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* AI Context if available */}
          {currentFlight.ai_context && Object.keys(currentFlight.ai_context).length > 0 && (
            <div className="p-5 rounded-xl border border-purple-200 bg-purple-50">
              <h3 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {t('aiContext')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {currentFlight.ai_context.brand_voice && (
                  <div>
                    <span className="text-purple-600 font-medium">{t('brandVoice')}:</span>
                    <p className="text-purple-800 mt-1">{currentFlight.ai_context.brand_voice}</p>
                  </div>
                )}
                {currentFlight.ai_context.target_audience && (
                  <div>
                    <span className="text-purple-600 font-medium">{t('targetAudience')}:</span>
                    <p className="text-purple-800 mt-1">{currentFlight.ai_context.target_audience}</p>
                  </div>
                )}
                {currentFlight.ai_context.hashtag_strategy && currentFlight.ai_context.hashtag_strategy.length > 0 && (
                  <div className="col-span-full">
                    <span className="text-purple-600 font-medium">{t('hashtagStrategy')}:</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {currentFlight.ai_context.hashtag_strategy.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-purple-200 text-purple-700 rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Goals if defined */}
          {currentFlight.goals && (currentFlight.goals.platforms?.length || currentFlight.goals.posting_frequency) && (
            <div className="p-5 rounded-xl border border-gray-200 bg-white">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {t('campaignGoals')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {currentFlight.goals.platforms && currentFlight.goals.platforms.length > 0 && (
                  <div>
                    <span className="text-gray-500">{t('platforms')}:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {currentFlight.goals.platforms.map((p, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs uppercase">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {currentFlight.goals.posting_frequency && (
                  <div>
                    <span className="text-gray-500">{t('frequency')}:</span>
                    <p className="text-gray-900 mt-1">{currentFlight.goals.posting_frequency}</p>
                  </div>
                )}
                {currentFlight.goals.post_types && Object.keys(currentFlight.goals.post_types).length > 0 && (
                  <div>
                    <span className="text-gray-500">{t('postTypes')}:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(currentFlight.goals.post_types).map(([type, count]) => (
                        <span key={type} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'posts' && (
        <div>
          {/* Status filter */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-gray-500">{t('filters.filter')}:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('filters.all')} ({flightPosts.length})
              </button>
              {(['draft', 'scheduled', 'published', 'failed'] as PostStatus[]).map((status) => {
                const config = POST_STATUS[status];
                return postsByStatus[status].length > 0 && (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors flex items-center gap-1.5 ${
                      statusFilter === status
                        ? `${config.bg} ${config.text} ring-1 ${config.ring}`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                    {t(`posts:status.${status}`)} ({postsByStatus[status].length})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Posts list */}
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500">{t('noPostsInFlight')}</p>
              {currentProject && (
                <Link
                  to={`/projects/${currentProject.id}`}
                  className="inline-block mt-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  {t('toProjectCalendar')}
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPosts.map((post) => {
                const postConfig = POST_STATUS[post.status];
                return (
                <div
                  key={post.id}
                  onClick={() => setSelectedPost(post)}
                  className={`rounded-lg border border-gray-200 border-l-[5px] ${postConfig.accent} ${postConfig.cardBg} p-4 hover:border-gray-300 hover:shadow-sm cursor-pointer transition-all`}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail - clickable to navigate to posts page */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/posts?post=${post.id}`);
                      }}
                      className="flex-shrink-0 hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 rounded-lg transition-all"
                      title={t('openInPosts')}
                    >
                      {post.media_urls && post.media_urls.length > 0 ? (
                        <img
                          src={getMediaUrl(post.media_urls[0])}
                          alt=""
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${postConfig.bg} ${postConfig.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${postConfig.dot}`} />
                          {t(`posts:status.${post.status}`)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {post.platforms?.join(', ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-gray-900 line-clamp-2">
                        {post.text || <span className="text-gray-400 italic">{t('noText')}</span>}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDateTime(post.scheduled_for)}
                        </span>
                        {post.media_urls && post.media_urls.length > 1 && (
                          <span>{t('moreMedia', { count: post.media_urls.length - 1 })}</span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'media' && (
        <div>
          {flightMedia.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500">{t('noMediaInFlight')}</p>
              <Link
                to="/media"
                className="inline-block mt-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                {t('toMediaLibrary')}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {flightMedia.map((file) => (
                <div
                  key={file.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <div className="aspect-square bg-gray-100 relative">
                    {file.mime_type.startsWith('image/') ? (
                      <img
                        src={getMediaUrl(file.url)}
                        alt={file.original_name}
                        className="w-full h-full object-cover"
                      />
                    ) : file.mime_type.startsWith('video/') ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <svg className="w-12 h-12 text-white/80" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}

                    {/* Source badge */}
                    {file.source && (
                      <div className="absolute top-2 right-2">
                        <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                          file.source === 'design' ? 'bg-blue-100 text-blue-700' :
                          file.source === 'stock' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {file.source === 'design' ? 'Design' :
                           file.source === 'stock' ? 'Stock' : 'Upload'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-xs text-gray-600 truncate">{file.original_name}</p>
                    <p className="text-xs text-gray-400">
                      {(file.size / 1024).toFixed(0)} KB
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Flight Modal */}
      {showEditModal && currentFlight && (
        <EditFlightModal
          flight={currentFlight}
          onClose={() => setShowEditModal(false)}
          onUpdated={() => fetchFlight(currentFlight.id)}
        />
      )}

      {/* Simple Post Detail View */}
      {selectedPost && (() => {
        const modalPostConfig = POST_STATUS[selectedPost.status];
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPost(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={`sticky top-0 bg-white border-b border-gray-200 border-t-4 ${modalPostConfig.accent} p-4 flex items-center justify-between rounded-t-xl`}>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${modalPostConfig.bg} ${modalPostConfig.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${modalPostConfig.dot}`} />
                  {t(`posts:status.${selectedPost.status}`)}
                </span>
                <span className="text-sm text-gray-500">
                  {selectedPost.platforms?.join(', ').toUpperCase()}
                </span>
              </div>
              <button onClick={() => setSelectedPost(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Media */}
              {selectedPost.media_urls && selectedPost.media_urls.length > 0 && (
                <div className="mb-4">
                  <div className={`grid gap-2 ${selectedPost.media_urls.length > 1 ? 'grid-cols-2' : ''}`}>
                    {selectedPost.media_urls.map((url, i) => (
                      <img
                        key={i}
                        src={getMediaUrl(url)}
                        alt=""
                        className="w-full rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:text', 'Text')}</label>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {selectedPost.text || <span className="text-gray-400 italic">{t('noText')}</span>}
                </p>
              </div>

              {/* Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:scheduledFor', 'Geplant für')}</label>
                <p className="text-gray-900">{formatDateTime(selectedPost.scheduled_for)}</p>
              </div>

              {/* Link to project */}
              {currentProject && (
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to={`/projects/${currentProject.id}`}
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {t('editInCalendar')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default FlightDetailPage;
